import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import ResumeUploader from "@/components/ResumeUploader";
import { submitApplication } from "@/lib/api";

const schema = z.object({
  full_name: z.string().min(2, "Please enter your full name"),
  phone: z.string().min(7, "Please enter a valid mobile number"),
  email: z.string().email("Please enter a valid email"),
  current_city: z.string().min(2, "Please enter your current city"),
  qualification: z.string().min(2, "Please enter your qualification"),
  total_experience: z.string().min(1, "Required"),
  current_salary: z.string().optional(),
  expected_salary: z.string().optional(),
  cover_letter: z.string().optional(),
});

export default function ApplyForm({ job, onSuccess, testIdPrefix = "apply" }) {
  const [resume, setResume] = useState(null);
  const [resumeError, setResumeError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const {
    register, handleSubmit, reset, formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: "", phone: "", email: "", current_city: "", qualification: "",
      total_experience: "", current_salary: "", expected_salary: "", cover_letter: "",
    },
  });

  const onSubmit = async (values) => {
    if (!resume) {
      setResumeError("Please upload your resume");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("job_id", job.id);
      Object.entries(values).forEach(([k, v]) => fd.append(k, v ?? ""));
      fd.append("resume", resume);
      await submitApplication(fd);
      toast.success("Application submitted successfully!");
      setSubmitted(true);
      reset();
      setResume(null);
      onSuccess?.();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not submit. Please try again.");
    }
  };

  if (submitted) {
    return (
      <div data-testid={`${testIdPrefix}-success`} className="text-center py-10">
        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        </div>
        <h3 className="mt-5 font-heading font-bold text-2xl text-slate-900">Application received!</h3>
        <p className="mt-3 text-sm text-slate-600 max-w-md mx-auto">
          Thank you for applying to <span className="font-semibold text-slate-900">{job.title}</span>.
          Our recruitment team will review your profile and reach out within 2 business days.
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          data-testid={`${testIdPrefix}-another`}
          className="mt-6 text-sm font-semibold text-primary hover:text-primary-hover"
        >
          Submit another application →
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-testid={`${testIdPrefix}-form`} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full Name *" error={errors.full_name?.message}>
          <Input data-testid={`${testIdPrefix}-name`} placeholder="e.g. Priya Sharma" {...register("full_name")} />
        </Field>
        <Field label="Mobile Number *" error={errors.phone?.message}>
          <Input data-testid={`${testIdPrefix}-phone`} placeholder="+91 ..." {...register("phone")} />
        </Field>
        <Field label="Email *" error={errors.email?.message}>
          <Input data-testid={`${testIdPrefix}-email`} type="email" placeholder="you@email.com" {...register("email")} />
        </Field>
        <Field label="Current City *" error={errors.current_city?.message}>
          <Input data-testid={`${testIdPrefix}-city`} placeholder="e.g. Mumbai" {...register("current_city")} />
        </Field>
        <Field label="Qualification *" error={errors.qualification?.message}>
          <Input data-testid={`${testIdPrefix}-qualification`} placeholder="e.g. B.E. Mechanical" {...register("qualification")} />
        </Field>
        <Field label="Experience *" error={errors.total_experience?.message}>
          <Input data-testid={`${testIdPrefix}-experience`} placeholder="e.g. 3 years" {...register("total_experience")} />
        </Field>
        <Field label="Current Salary">
          <Input data-testid={`${testIdPrefix}-current-salary`} placeholder="e.g. ₹3.5 LPA" {...register("current_salary")} />
        </Field>
        <Field label="Expected Salary">
          <Input data-testid={`${testIdPrefix}-expected-salary`} placeholder="e.g. ₹5 LPA" {...register("expected_salary")} />
        </Field>
      </div>

      <Field label="Cover Note (optional)">
        <Textarea data-testid={`${testIdPrefix}-cover`} rows={3} placeholder="Why are you a great fit for this role?" {...register("cover_letter")} />
      </Field>

      <div>
        <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Resume *</Label>
        <div className="mt-1.5">
          <ResumeUploader
            value={resume}
            onChange={(f, err) => { setResume(f); setResumeError(err); }}
            error={resumeError}
            testId={`${testIdPrefix}-resume`}
          />
        </div>
      </div>

      <Button
        type="submit"
        data-testid={`${testIdPrefix}-submit`}
        disabled={isSubmitting}
        className="w-full sm:w-auto bg-primary hover:bg-primary-hover text-white font-semibold px-7 py-6 rounded-lg shadow-sm"
      >
        {isSubmitting ? (
          <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Submitting…</span>
        ) : (
          <span className="inline-flex items-center gap-2"><Send className="w-4 h-4" />Submit Application</span>
        )}
      </Button>
      <p className="text-xs text-slate-500">By submitting, you agree to be contacted about this opening.</p>
    </form>
  );
}

function Field({ label, error, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</Label>
      {children}
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
